import { useId, useState } from "react";
import { AdjustmentsIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

const groupByItems: ReadonlyArray<PickerItemData<string>> = [
    { value: "none", label: "None" },
    { value: "status", label: "Status" },
    { value: "assignee", label: "Assignee" },
];

const densities = ["low", "medium", "high"] as const;
type Density = (typeof densities)[number];

const densityLabels: Record<Density, string> = {
    low: "Low",
    medium: "Medium",
    high: "High",
};

/** Sandbox for the settings panel shell: every part in one open-able panel. */
export const SettingsPanelPlayground = () => {
    const groupByLabelId = useId();
    const densityLabelId = useId();
    const [groupBy, setGroupBy] = useState("status");
    const [density, setDensity] = useState<Density>("medium");
    const [isShowingEmpty, setIsShowingEmpty] = useState(false);
    const [isChanged, setIsChanged] = useState(true);

    return (
        <SandboxLayout>
            <ShowcasePreview summary={isChanged ? "Changed" : "Default"}>
                <SettingsPanel.Root
                    trigger={
                        <Button
                            variant="primary"
                            size="md"
                            iconOnly
                            aria-label="Configure columns"
                        >
                            <AdjustmentsIcon />
                        </Button>
                    }
                    triggerTooltip="Change and rearrange visible columns"
                    footer={
                        isChanged && (
                            <SettingsPanel.Action
                                onClick={() => setIsChanged(false)}
                            >
                                Reset
                            </SettingsPanel.Action>
                        )
                    }
                >
                    <SettingsPanel.Section label="Shown in table">
                        <SettingsPanel.Row label="Date" />
                        <SettingsPanel.Row label="A very long column name that runs past the panel's width cap" />
                        <SettingsPanel.Row
                            label="Group by"
                            labelId={groupByLabelId}
                        >
                            <Picker.Select
                                items={groupByItems}
                                value={groupBy}
                                onValueChange={(next) => {
                                    if (next !== null) {
                                        setGroupBy(next);
                                    }
                                }}
                                trigger={
                                    <Picker.Button
                                        variant="ghost"
                                        aria-labelledby={groupByLabelId}
                                    />
                                }
                            />
                        </SettingsPanel.Row>
                    </SettingsPanel.Section>
                    <SettingsPanel.Section label="Hidden in table">
                        <SettingsPanel.Empty>
                            All columns are visible
                        </SettingsPanel.Empty>
                    </SettingsPanel.Section>
                    <SettingsPanel.Separator />
                    <SettingsPanel.Row label="Density" labelId={densityLabelId}>
                        <ToggleGroup.Root<Density>
                            size="sm"
                            aria-labelledby={densityLabelId}
                            value={density}
                            onValueChange={(next) => {
                                if (next !== null) {
                                    setDensity(next);
                                }
                            }}
                        >
                            {densities.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {densityLabels[option]}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </SettingsPanel.Row>
                    <SettingsPanel.ToggleRow
                        label="Show empty columns"
                        checked={isShowingEmpty}
                        onCheckedChange={setIsShowingEmpty}
                    />
                </SettingsPanel.Root>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
